import { useState, type ReactElement } from 'react';
import { plural } from '@tms/i18n';
import { api, type PickupOperation } from '../shared/api/endpoints';
import { useLocale } from '../shared/i18n/locale';
import { useAsync } from '../shared/lib/use-async';
import { EmptyState, primaryButtonStyle } from '../shared/ui/primitives';

/**
 * Матрица «точка отправления × водители» (разд. 12, FR-627).
 *
 * Экран администратора, но диспетчеру он доступен на чтение — и это не послабление:
 * когда заказ некому отдать, первая мысль диспетчера — посмотреть, кто вообще
 * закреплён за этой аптекой.
 *
 * Изменения копятся и применяются одним действием: закрепление меняет то, кому
 * достанутся заказы, и частично применённое выделение хуже неприменённого.
 */
export function PickupMatrixPage({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const { locale, messages } = useLocale();
  const summary = messages.cabinet.matrix;
  const matrix = useAsync(() => api.pickupMatrix(), []);
  const drivers = useAsync(() => api.drivers(), []);

  const [pending, setPending] = useState<Map<string, PickupOperation>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const locations = matrix.data?.locations ?? [];
  const driverList = drivers.data?.data ?? [];

  const toggle = (driverId: string, locationId: string, attached: boolean): void => {
    const key = `${driverId}:${locationId}`;

    setPending((current) => {
      const next = new Map(current);
      const existing = next.get(key);

      if (existing !== undefined) {
        next.delete(key);
        return next;
      }

      next.set(key, {
        driver_id: driverId,
        location_id: locationId,
        action: attached ? 'detach' : 'attach',
      });

      return next;
    });
  };

  const apply = async (): Promise<void> => {
    setError(null);

    try {
      await api.applyPickupMatrix([...pending.values()]);
      setPending(new Map());
      matrix.reload();
    } catch (cause) {
      setError(String(cause));
    }
  };

  if (locations.length === 0 || driverList.length === 0) {
    return <EmptyState text={messages.cabinet.common.empty} />;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{messages.cabinet.matrix.title}</h1>
        {canEdit ? (
          <button
            type="button"
            style={primaryButtonStyle}
            disabled={pending.size === 0}
            onClick={() => void apply()}
          >
            {`${messages.cabinet.matrix.apply} ${pending.size}`}
          </button>
        ) : null}
      </header>

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={headStyle}>{messages.cabinet.directories.name}</th>
              {locations.map((location) => (
                <th key={location.location_id} style={{ ...headStyle, textAlign: 'center' }}>
                  <div style={{ display: 'grid', gap: '2px' }}>
                    <span>{location.name}</span>
                    {/*
                     * Сводка по точке: заказы в работе и водители на смене. Числа
                     * подписаны в подсказке с согласованием по числу (FR-557):
                     * «1 заказ в работе», но «5 заказов в работе».
                     */}
                    <span
                      className="num"
                      style={{ fontWeight: 'var(--fw-body)', color: 'var(--text-faint)' }}
                      title={[
                        plural(locale, location.orders_in_progress ?? 0, summary.ordersInProgress),
                        plural(locale, location.drivers_on_shift ?? 0, summary.driversOnShift),
                      ].join(' · ')}
                    >
                      {`${location.orders_in_progress ?? 0} / ${location.drivers_on_shift ?? 0}`}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {driverList.map((driver) => (
              <tr key={driver.id} style={{ height: 'var(--row-h-dense)' }}>
                <td style={cellStyle}>{driver.full_name}</td>

                {locations.map((location) => {
                  const attached = (location.driver_ids ?? []).includes(driver.id);
                  const key = `${driver.id}:${location.location_id}`;
                  const changed = pending.has(key);

                  return (
                    <td
                      key={location.location_id}
                      style={{
                        ...cellStyle,
                        textAlign: 'center',
                        background: changed ? 'var(--accent-weak)' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={changed ? !attached : attached}
                        onChange={() => toggle(driver.id, location.location_id ?? '', attached)}
                        aria-label={`${driver.full_name} — ${location.name}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const headStyle = {
  textAlign: 'left' as const,
  fontSize: 'var(--fs-caption)',
  fontWeight: 'var(--fw-label)',
  color: 'var(--text-muted)',
  padding: 'var(--sp-2) var(--sp-3)',
  borderBottom: '1px solid var(--border)',
};

const cellStyle = {
  padding: 'var(--sp-1) var(--sp-3)',
  borderBottom: '1px solid var(--border)',
};
